import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Form,
    Input,
    Label,
    Modal,
    ModalBody,
    Offcanvas,
    OffcanvasBody,
    Row,
    UncontrolledDropdown,
    FormFeedback,
} from 'reactstrap';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import DeleteModal from '../../../Components/Common/DeleteModal';
import { ToastContainer } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import {
    fetchTeamDataRequest,
    addTeamDataRequest,
    updateTeamDataRequest,
    deleteTeamDataRequest,
    toggleActiveRequest,
} from '../../../store/team/action';
import admin from '../../../assets/images/users/avatar_admin.png';
import editor from '../../../assets/images/users/avatar_editor.png';
import support from '../../../assets/images/users/avatar_support.png';

const Team = () => {
    const { t } = useTranslation();
    document.title = t('team.title');

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { teamData, status } = useSelector((state) => state.Team);
    const { user } = useSelector((state) => ({
        user: state.Login.user,
    }));
    const [teamList, setTeamList] = useState([]);
    const [deleteModal, setDeleteModal] = useState(false);
    const [teamMem, setTeamMem] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [modal, setModal] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [sideBar, setSideBar] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [viewMode, setViewMode] = useState('grid');
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [pfpImage, setPfpImage] = useState(null);

    useEffect(() => {
        dispatch(fetchTeamDataRequest({ dispatch, navigate }));
    }, [dispatch, navigate]);

    useEffect(() => {
        const updatedTeamData = teamData.map((item) => ({
            ...item,
        }));
        setTeamList(updatedTeamData);
    }, [teamData]);

    const toggle = useCallback(() => {
        setModal((prev) => !prev);
        if (modal) {
            setTeamMem(null);
            setPfpImage(null);
        }
    }, [modal]);

    const handleTeamClick = useCallback(
        (teamMem) => {
            setTeamMem({
                id: teamMem.id,
                firstname: teamMem.firstname,
                lastname: teamMem.lastname,
                phoneNumber: teamMem.phoneNumber,
                email: teamMem.email,
                role: teamMem.role,
                twoFactorEnabled: teamMem.twoFactorEnabled,
                isActive: teamMem.isActive,
            });
            setPfpImage(teamMem.pfpimage);
            setIsEdit(true);
            toggle();
        },
        [toggle]
    );

    const handleTeamClicks = () => {
        setTeamMem(null);
        setPfpImage(null);
        setIsEdit(false);
        toggle();
    };

    const onClickData = (team) => {
        setTeamMem(team);
        setDeleteModal(true);
    };

    const handleDeleteTeamData = () => {
        if (teamMem) {
            dispatch(deleteTeamDataRequest({ id: teamMem.id, dispatch, navigate }));
            setDeleteModal(false);
        }
    };

    const handleViewToggle = (mode) => {
        setViewMode(mode);
    };

    const handleToggleActive = (id, isActive) => {
        dispatch(toggleActiveRequest({ id, isActive: !isActive, dispatch, navigate }));
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleRoleFilter = (role) => {
        setRoleFilter(role);
        setDropdownOpen(false);
    };

    const handlePfpImageChange = (e) => {
        setPfpImage(e.target.files[0]);
    };

    const toggledropDown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    const filteredTeamList = teamList.filter((member) => {
        const matchesSearch =
            member.firstname.toLowerCase().includes(searchQuery.toLowerCase()) ||
            member.lastname.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesRole = roleFilter === 'all' || member.role === roleFilter;
        const isNotCurrentUser = user ? member.email !== user.email : true;
        return matchesSearch && matchesRole && isNotCurrentUser;
    });

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            firstname: (teamMem && teamMem.firstname) || '',
            lastname: (teamMem && teamMem.lastname) || '',
            phoneNumber: (teamMem && teamMem.phoneNumber) || '',
            email: (teamMem && teamMem.email) || '',
            role: (teamMem && teamMem.role) || 'editor',
            twoFactorEnabled: (teamMem && teamMem.twoFactorEnabled) || false,
            isActive: (teamMem && teamMem.isActive) || true,
        },
        validationSchema: Yup.object({
            firstname: Yup.string().required(t('team.validation.firstname')),
            lastname: Yup.string().required(t('team.validation.lastname')),
            phoneNumber: Yup.string()
                .matches(/^\d{8}$/, t('team.validation.phoneNumber_format'))
                .required(t('team.validation.phoneNumber_required')),
            email: Yup.string().email(t('team.validation.email_format')).required(t('team.validation.email_required')),
            role: Yup.string().oneOf(['admin', 'support', 'editor']).required(t('team.validation.role')),
        }),
        onSubmit: async (values) => {
            const formData = new FormData();
            formData.append('firstname', values.firstname);
            formData.append('lastname', values.lastname);
            formData.append('phoneNumber', values.phoneNumber);
            formData.append('email', values.email);
            formData.append('role', values.role);
            formData.append('twoFactorEnabled', values.twoFactorEnabled);
            formData.append('isActive', values.isActive);

            if (isEdit) {
                dispatch(updateTeamDataRequest({ id: teamMem.id, data: values, dispatch, navigate }));
            } else {
                dispatch(addTeamDataRequest({ data: values, dispatch, navigate }));
            }
            validation.resetForm();
            setPfpImage(null);
            toggle();
        },
    });

    return (
        <React.Fragment>
            <ToastContainer />
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title={t('team.title')} pageTitle={t('team.page_title')} />
                    <Card>
                        <CardBody>
                            <Row className="g-2">
                                <Col sm={4}>
                                    <div className="search-box">
                                        <Input
                                            type="text"
                                            className="form-control"
                                            placeholder={t('team.search.placeholder')}
                                            value={searchQuery}
                                            onChange={handleSearch}
                                        />
                                        <i className="ri-search-line search-icon"></i>
                                    </div>
                                </Col>
                                <Col className="col-sm-auto ms-auto">
                                    <div className="list-grid-nav hstack gap-1">
                                        <Button
                                            color="info"
                                            id="grid-view-button"
                                            className={`btn btn-soft-info nav-link btn-icon fs-14 ${
                                                viewMode === 'grid' ? 'active' : ''
                                            } filter-button shadow-none`}
                                            onClick={() => handleViewToggle('grid')}
                                        >
                                            <i className="ri-grid-fill"></i>
                                        </Button>
                                        <Button
                                            color="info"
                                            id="list-view-button"
                                            className={`btn btn-soft-info nav-link btn-icon fs-14 ${
                                                viewMode === 'list' ? 'active' : ''
                                            } filter-button shadow-none`}
                                            onClick={() => handleViewToggle('list')}
                                        >
                                            <i className="ri-list-unordered"></i>
                                        </Button>
                                        <Dropdown isOpen={dropdownOpen} toggle={toggledropDown}>
                                            <DropdownToggle type="button" className="btn btn-soft-info btn-icon fs-14 shadow-none">
                                                <i className="ri-more-2-fill"></i>
                                            </DropdownToggle>
                                            <DropdownMenu>
                                                <DropdownItem onClick={() => handleRoleFilter('all')}>{t('team.filter.all')}</DropdownItem>
                                                <DropdownItem onClick={() => handleRoleFilter('admin')}>{t('team.filter.admin')}</DropdownItem>
                                                <DropdownItem onClick={() => handleRoleFilter('support')}>{t('team.filter.support')}</DropdownItem>
                                                <DropdownItem onClick={() => handleRoleFilter('editor')}>{t('team.filter.editor')}</DropdownItem>
                                            </DropdownMenu>
                                        </Dropdown>
                                        <Button color="success" onClick={handleTeamClicks}>
                                            <i className="ri-add-fill me-1 align-bottom"></i> {t('team.add_members')}
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>

                    <Row>
                        <Col lg={12}>
                            <div id="teamlist">
                                {status === 'loading' ? (
                                    <div>{t('team.loading')}</div>
                                ) : (
                                    <Row className={`team-list ${viewMode === 'list' ? 'list-view-filter' : 'grid-view-filter'}`}>
                                        {filteredTeamList.length === 0 ? (
                                            <div className="py-4 mt-4 text-center" id="noresult">
                                                <lord-icon
                                                    src="https://cdn.lordicon.com/msoeawqm.json"
                                                    trigger="loop"
                                                    colors="primary:#405189,secondary:#0ab39c"
                                                    style={{ width: '72px', height: '72px' }}
                                                ></lord-icon>
                                                <h5 className="mt-4">{t('team.no_result')}</h5>
                                            </div>
                                        ) : (
                                            filteredTeamList.map((item, key) => (
                                                <Col key={key} xs={12} sm={viewMode === 'grid' ? 6 : 12} md={viewMode === 'grid' ? 4 : 12}>
                                                    <Card className="team-box">
                                                        <CardBody className="p-4">
                                                            <Row className="align-items-center team-row">
                                                                <Col className="team-settings">
                                                                    <Row>
                                                                        <UncontrolledDropdown direction="start" className="col text-end">
                                                                            <DropdownToggle tag="a" role="button">
                                                                                <i className="ri-more-fill fs-17"></i>
                                                                            </DropdownToggle>
                                                                            <DropdownMenu>
                                                                                <DropdownItem onClick={() => handleTeamClick(item)}>
                                                                                    <i className="ri-pencil-line me-2 align-bottom text-muted"></i>
                                                                                    {t('team.actions.edit')}
                                                                                </DropdownItem>
                                                                                <DropdownItem onClick={() => onClickData(item)}>
                                                                                    <i className="ri-delete-bin-5-line me-2 align-bottom text-muted"></i>
                                                                                    {t('team.actions.remove')}
                                                                                </DropdownItem>
                                                                                <DropdownItem onClick={() => handleToggleActive(item.id, item.isActive)}>
                                                                                    <i
                                                                                        className={`ri-${
                                                                                            item.isActive ? 'lock' : 'unlock'
                                                                                        }-line me-2 align-bottom text-muted`}
                                                                                    ></i>
                                                                                    {t(`team.actions.${item.isActive ? 'deactivate' : 'activate'}`)}
                                                                                </DropdownItem>
                                                                            </DropdownMenu>
                                                                        </UncontrolledDropdown>
                                                                    </Row>
                                                                </Col>
                                                                <Col className="col">
                                                                    <div className="team-profile-img">
                                                                        <div className="avatar-lg img-thumbnail rounded-circle flex-shrink-0">
                                                                            <img
                                                                                src={item.role === 'admin' ? admin : item.role === 'editor' ? editor : support}
                                                                                alt=""
                                                                                className="img-fluid d-block rounded-circle"
                                                                            />
                                                                        </div>
                                                                        <div className="team-content">
                                                                            <Link to="#" onClick={() => { setIsOpen(true); setSideBar(item); }}>
                                                                                <h5 className="fs-16 mb-1">{item.firstname} {item.lastname}</h5>
                                                                            </Link>
                                                                            <p className="text-muted mb-0">{t(`team.roles.${item.role}`)}</p>
                                                                            {viewMode === 'list' && (
                                                                                <div className="d-flex gap-3 mt-2">
                                                                                    <p className="text-muted mb-0">
                                                                                        <strong>{t('team.details.phone')}:</strong> {item.phoneNumber}
                                                                                    </p>
                                                                                    <p className="text-muted mb-0">
                                                                                        <strong>{t('team.details.email')}:</strong> {item.email}
                                                                                    </p>
                                                                                    <p className="text-muted mb-0">
                                                                                        <strong>{t('team.details.2fa')}:</strong>{' '}
                                                                                        {t(`team.details.2fa_${item.twoFactorEnabled ? 'enabled' : 'disabled'}`)}
                                                                                    </p>
                                                                                    <p className="text-muted mb-0">
                                                                                        <strong>{t('team.details.account')}:</strong>{' '}
                                                                                        {t(`team.details.account_${item.isActive ? 'active' : 'inactive'}`)}
                                                                                    </p>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                                {viewMode === 'grid' && (
                                                                    <Col className="mt-3">
                                                                        <p className="text-muted mb-1">
                                                                            <strong>{t('team.details.phone')}:</strong> {item.phoneNumber}
                                                                        </p>
                                                                        <p className="text-muted mb-1">
                                                                            <strong>{t('team.details.email')}:</strong> {item.email}
                                                                        </p>
                                                                        <p className="text-muted mb-0">
                                                                            <strong>{t('team.details.2fa')}:</strong>{' '}
                                                                            {t(`team.details.2fa_${item.twoFactorEnabled ? 'enabled' : 'disabled'}`)}
                                                                        </p>
                                                                    </Col>
                                                                )}
                                                            </Row>
                                                        </CardBody>
                                                    </Card>
                                                </Col>
                                            ))
                                        )}
                                    </Row>
                                )}
                            </div>
                        </Col>
                    </Row>

                    <Modal id="addmembers" isOpen={modal} toggle={toggle} centered>
                        <ModalBody>
                            <Form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    validation.handleSubmit();
                                    return false;
                                }}
                            >
                                <Row>
                                    <Col lg={12}>
                                        <div className="mb-3">
                                            <Label htmlFor="firstname" className="form-label">
                                                {t('team.form.firstname')}
                                            </Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                id="firstname"
                                                placeholder={t('team.form.firstname_placeholder')}
                                                name="firstname"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.firstname}
                                                invalid={validation.touched.firstname && validation.errors.firstname}
                                            />
                                            {validation.touched.firstname && validation.errors.firstname && (
                                                <FormFeedback>{validation.errors.firstname}</FormFeedback>
                                            )}
                                        </div>
                                        <div className="mb-3">
                                            <Label htmlFor="lastname" className="form-label">
                                                {t('team.form.lastname')}
                                            </Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                id="lastname"
                                                placeholder={t('team.form.lastname_placeholder')}
                                                name="lastname"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.lastname}
                                                invalid={validation.touched.lastname && validation.errors.lastname}
                                            />
                                            {validation.touched.lastname && validation.errors.lastname && (
                                                <FormFeedback>{validation.errors.lastname}</FormFeedback>
                                            )}
                                        </div>
                                        <div className="mb-3">
                                            <Label htmlFor="phoneNumber" className="form-label">
                                                {t('team.form.phoneNumber')}
                                            </Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                id="phoneNumber"
                                                placeholder={t('team.form.phoneNumber_placeholder')}
                                                name="phoneNumber"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.phoneNumber}
                                                invalid={validation.touched.phoneNumber && validation.errors.phoneNumber}
                                            />
                                            {validation.touched.phoneNumber && validation.errors.phoneNumber && (
                                                <FormFeedback>{validation.errors.phoneNumber}</FormFeedback>
                                            )}
                                        </div>
                                        <div className="mb-3">
                                            <Label htmlFor="email" className="form-label">
                                                {t('team.form.email')}
                                            </Label>
                                            <Input
                                                type="email"
                                                className="form-control"
                                                id="email"
                                                placeholder={t('team.form.email_placeholder')}
                                                name="email"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.email}
                                                invalid={validation.touched.email && validation.errors.email}
                                            />
                                            {validation.touched.email && validation.errors.email && (
                                                <FormFeedback>{validation.errors.email}</FormFeedback>
                                            )}
                                        </div>
                                        <div className="mb-3">
                                            <Label htmlFor="role" className="form-label">
                                                {t('team.form.role')}
                                            </Label>
                                            <Input
                                                type="select"
                                                className="form-control"
                                                id="role"
                                                name="role"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.role}
                                                invalid={validation.touched.role && validation.errors.role}
                                            >
                                                <option value="admin">{t('team.roles.admin')}</option>
                                                <option value="support">{t('team.roles.support')}</option>
                                                <option value="editor">{t('team.roles.editor')}</option>
                                            </Input>
                                            {validation.touched.role && validation.errors.role && (
                                                <FormFeedback>{validation.errors.role}</FormFeedback>
                                            )}
                                        </div>
                                    </Col>
                                    <Col lg={12}>
                                        <div className="hstack gap-2 justify-content-end">
                                            <button type="button" className="btn btn-light" onClick={toggle}>
                                                {t('team.form.close')}
                                            </button>
                                            <button type="submit" className="btn btn-success" id="addNewMember">
                                                {t(`team.form.${isEdit ? 'save' : 'add_member'}`)}
                                            </button>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                        </ModalBody>
                    </Modal>

                    <Offcanvas
                        isOpen={isOpen}
                        direction="end"
                        toggle={() => setIsOpen(!isOpen)}
                        className="offcanvas-end border-0"
                    >
                        <OffcanvasBody className="profile-offcanvas p-0">
                            <div className="p-3">
                                <div className="team-settings">
                                    <Row>
                                        <UncontrolledDropdown direction="start" className="col text-end">
                                            <DropdownToggle tag="a" role="button">
                                                <i className="ri-more-fill fs-17"></i>
                                            </DropdownToggle>
                                            <DropdownMenu>
                                                <DropdownItem onClick={() => sideBar && handleTeamClick(sideBar)}>
                                                    <i className="ri-pencil-line me-2 align-middle"></i>{t('team.actions.edit')}
                                                </DropdownItem>
                                                <DropdownItem onClick={() => sideBar && onClickData(sideBar)}>
                                                    <i className="ri-delete-bin-5-line me-2 align-middle"></i>{t('team.actions.delete')}
                                                </DropdownItem>
                                                <DropdownItem onClick={() => sideBar && handleToggleActive(sideBar.id, sideBar.isActive)}>
                                                    <i
                                                        className={`ri-${sideBar?.isActive ? 'lock' : 'unlock'}-line me-2 align-middle`}
                                                    ></i>
                                                    {t(`team.actions.${sideBar?.isActive ? 'deactivate' : 'activate'}`)}
                                                </DropdownItem>
                                            </DropdownMenu>
                                        </UncontrolledDropdown>
                                    </Row>
                                </div>
                            </div>
                            <div className="p-3 text-center">
                                <img
                                    src={sideBar?.role === 'admin' ? admin : sideBar?.role === 'editor' ? editor : support}
                                    alt=""
                                    className="avatar-lg img-thumbnail rounded-circle mx-auto"
                                />
                                <div className="mt-3">
                                    <h5 className="fs-15">
                                        <Link to="#" className="link-primary">
                                            {sideBar?.firstname} {sideBar?.lastname || t('team.sidebar.unknown')}
                                        </Link>
                                    </h5>
                                    <p className="text-muted">{t(`team.roles.${sideBar?.role || 'na'}`)}</p>
                                    <p className="text-muted">{t(`team.details.account_${sideBar?.isActive ? 'active' : 'inactive'}`)}</p>
                                </div>
                                <div className="hstack gap-2 justify-content-center mt-4">
                                    <div className="avatar-xs">
                                        <Link to="#" className="avatar-title bg-soft-secondary text-secondary rounded fs-16">
                                            <i className="ri-facebook-fill"></i>
                                        </Link>
                                    </div>
                                    <div className="avatar-xs">
                                        <Link to="#" className="avatar-title bg-soft-success text-success rounded fs-16">
                                            <i className="ri-slack-fill"></i>
                                        </Link>
                                    </div>
                                    <div className="avatar-xs">
                                        <Link to="#" className="avatar-title bg-soft-info text-info rounded fs-16">
                                            <i className="ri-linkedin-fill"></i>
                                        </Link>
                                    </div>
                                    <div className="avatar-xs">
                                        <Link to="#" className="avatar-title bg-soft-danger text-danger rounded fs-16">
                                            <i className="ri-dribbble-fill"></i>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                            <Row className="g-0 text-center">
                                <div className="p-3">
                                    <h5 className="fs-15 mb-3">{t('team.sidebar.personal_details')}</h5>
                                    <div className="mb-3">
                                        <p className="text-muted text-uppercase fw-semibold fs-12 mb-2">{t('team.sidebar.number')}</p>
                                        <h6>{sideBar?.phoneNumber || t('team.sidebar.na')}</h6>
                                    </div>
                                    <div className="mb-3">
                                        <p className="text-muted text-uppercase fw-semibold fs-12 mb-2">{t('team.sidebar.email')}</p>
                                        <h6>{sideBar?.email || t('team.sidebar.na')}</h6>
                                    </div>
                                    <div className="mb-3">
                                        <p className="text-muted text-uppercase fw-semibold fs-12 mb-2">{t('team.sidebar.2fa')}</p>
                                        <h6>{t(`team.details.2fa_${sideBar?.twoFactorEnabled ? 'enabled' : 'disabled'}`)}</h6>
                                    </div>
                                </div>
                            </Row>
                        </OffcanvasBody>
                        <div className="offcanvas-footer border p-3 hstack gap-3 text-center position-relative">
                            <button className="btn btn-light w-100">
                                <i className="ri-question-answer-fill align-bottom ms-1"></i> {t('team.sidebar.send_message')}
                            </button>
                        </div>
                    </Offcanvas>
                </Container>
            </div>
            <DeleteModal
                show={deleteModal}
                onDeleteClick={handleDeleteTeamData}
                onCloseClick={() => setDeleteModal(false)}
            />
            <ToastContainer closeButton={false} />
        </React.Fragment>
    );
};

export default Team;